import React, { Suspense, lazy } from 'react';
import { Competition } from '../types';
import { Loader2, Trophy } from 'lucide-react';

const StripeProductCard = lazy(() => import('./StripeProductCard').then(module => ({ default: module.StripeProductCard })));

interface LazyStripeProductCardProps {
  competition: Competition;
  className?: string;
}

const StripeLoadingFallback: React.FC<{ competition: Competition; className?: string }> = ({ competition, className = '' }) => {
  const entryFeeAmount = competition.entryFeeAmount || 0;
  const entryFeeCurrency = (competition.entryFeeCurrency || 'gbp').toLowerCase();

  // Check if we're returning from a payment
  const urlParams = new URLSearchParams(window.location.search);
  const isReturningFromPayment = urlParams.get('payment_status') === 'verifying' ||
                                  urlParams.has('payment_intent') ||
                                  urlParams.get('redirect_status') === 'succeeded';

  const formatPrice = () => {
    const amount = entryFeeAmount / 100;
    const currencySymbol = entryFeeCurrency === 'gbp' ? '£' : entryFeeCurrency === 'usd' ? '$' : '€';
    return `${currencySymbol}${amount.toFixed(2)}`;
  };

  return (
    <div className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden hover:shadow-xl transition-all duration-300 ${className}`}>
      <div className="bg-gradient-to-r from-theme-primary to-theme-secondary p-3 sm:p-4">
        <div className="flex items-center space-x-2.5">
          <div className="bg-white/20 p-2 rounded-lg">
            <Trophy className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              {competition.name}
            </h3>
            <p className="text-white/80 text-xs sm:text-sm">
              Join the competition
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="text-center mb-6">
          <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            {formatPrice()}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            one-time entry fee
          </div>
        </div>

        {isReturningFromPayment ? (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <div className="flex items-center space-x-3">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-blue-700 dark:text-blue-300 font-semibold">
                  Confirming your payment...
                </p>
                <p className="text-blue-600 dark:text-blue-400 text-sm">
                  Please wait while we verify your payment
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading payment options...</span>
          </div>
        )}

        <div className="mt-6 space-y-3 opacity-50">
          <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Join the competition</span>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Make weekly picks</span>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Compete with other players</span>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Track your progress</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LazyStripeProductCard: React.FC<LazyStripeProductCardProps> = (props) => {
  return (
    <Suspense fallback={<StripeLoadingFallback competition={props.competition} className={props.className} />}>
      <StripeProductCard {...props} />
    </Suspense>
  );
};
